/**
 * useWearableCaptures — Fetch WearableCapture entities for the current actor.
 *
 * Uses the new capturesForActor query (added in wearables.graphql Phase 4a).
 * Falls back to the fan-out approach (sessionsForActor → capturesForSession)
 * if the direct query is unavailable during migration.
 *
 * actorId is the sequential actor_global_id from useAuth() (e.g., "17_2").
 * DRR-105: actorId is InternalId format — never a Snowflake.
 * Repository pattern: data access via Apollo hooks only.
 */

import { useQuery, useLazyQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useAuth } from '../../security/useAuth';

// ============================================================
// GraphQL
// ============================================================

const CAPTURES_FOR_ACTOR = gql`
  query CapturesForActor($actorId: ID!, $limit: Int, $importStatus: WearableCaptureImportStatus) {
    capturesForActor(actorId: $actorId, limit: $limit, importStatus: $importStatus) {
      id
      sessionId
      captureType
      contentUri
      contentUrl
      transcription
      barcodeValue
      capturedAt
      importStatus
      importedDocumentId
      importedAt
      metadata
    }
  }
`;

const SESSIONS_FOR_ACTOR = gql`
  query SessionsForActor($actorId: ID!, $limit: Int!) {
    sessionsForActor(actorId: $actorId, limit: $limit) {
      id
      deviceId
      actorId
      taskId
      residentId
      status
      startedAt
      location
    }
  }
`;

const CAPTURES_FOR_SESSION = gql`
  query CapturesForSession($sessionId: ID!) {
    capturesForSession(sessionId: $sessionId) {
      id
      sessionId
      captureType
      contentUri
      contentUrl
      transcription
      barcodeValue
      capturedAt
      importStatus
      importedDocumentId
      importedAt
      metadata
    }
  }
`;

// ============================================================
// Types
// ============================================================

export interface WearableCaptureRecord {
  id: string;
  sessionId: string;
  captureType: 'PHOTO' | 'VIDEO' | 'VOICE_NOTE' | 'BARCODE_SCAN';
  contentUri: string | null;
  contentUrl: string | null;
  transcription: string | null;
  barcodeValue: string | null;
  capturedAt: string;
  importStatus: 'UNIMPORTED' | 'IMPORTED';
  importedDocumentId: string | null;
  importedAt: string | null;
  metadata: Record<string, unknown> | null;
}

export interface WearableSessionRecord {
  id: string;
  deviceId: string;
  actorId: string;
  taskId: string | null;
  residentId: string | null;
  status: 'ACTIVE' | 'PAUSED' | 'ENDED';
  startedAt: string;
  location: string | null;
}

export interface UseWearableCapturesReturn {
  captures: WearableCaptureRecord[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

// ============================================================
// Hook
// ============================================================

export function useWearableCaptures(
  importStatusFilter: 'UNIMPORTED' | 'IMPORTED' | undefined = 'UNIMPORTED'
): UseWearableCapturesReturn {
  const { user } = useAuth();
  const actorId = user?.id ?? null;

  const { data, loading, error, refetch } = useQuery<{
    capturesForActor: WearableCaptureRecord[];
  }>(CAPTURES_FOR_ACTOR, {
    variables: { actorId, limit: 20, importStatus: importStatusFilter },
    skip: !actorId,
    fetchPolicy: 'cache-and-network',
  });

  return {
    captures: data?.capturesForActor ?? [],
    loading,
    error: error as Error | undefined,
    refetch,
  };
}
